
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Calendar, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Location {
  id?: string;
  name: string;
  latitude: number;
  longitude: number;
  created_at?: string;
}

interface LocationDisplayCardProps {
  location: Location;
  user: unknown;
  isFavoriteDialogOpen: boolean;
  setIsFavoriteDialogOpen: (open: boolean) => void;
  favoriteFormData: {
    favoriteName: string;
    notes: string;
  };
  setFavoriteFormData: React.Dispatch<React.SetStateAction<{
    favoriteName: string;
    notes: string;
  }>>;
  onStarLocation: () => void;
}

export const LocationDisplayCard: React.FC<LocationDisplayCardProps> = ({
  location,
  user,
  isFavoriteDialogOpen,
  setIsFavoriteDialogOpen,
  favoriteFormData,
  setFavoriteFormData,
  onStarLocation
}) => {
  const { user: authUser } = useAuth();
  const [isActuallyFavorited, setIsActuallyFavorited] = useState(false);
  const [checkingFavorite, setCheckingFavorite] = useState(true);

  // Check if this location is actually in the user's favorites
  useEffect(() => {
    const checkIfFavorite = async () => {
      if (!authUser || !location) {
        setIsActuallyFavorited(false);
        setCheckingFavorite(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('favorite_locations')
          .select('id')
          .eq('user_id', authUser.id)
          .eq('latitude', location.latitude)
          .eq('longitude', location.longitude)
          .maybeSingle();

        if (error) {
          console.error('Error checking if location is favorite:', error);
          setIsActuallyFavorited(false);
        } else {
          setIsActuallyFavorited(!!data);
        }
      } catch (error) {
        console.error('Error checking favorite status:', error);
        setIsActuallyFavorited(false);
      } finally {
        setCheckingFavorite(false);
      }
    };

    checkIfFavorite();
  }, [authUser, location.latitude, location.longitude]);

  return (
    <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-gold/30">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gold rounded-full">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-800">{location.name}</h3>
              <p className="text-sm text-amber-600">
                {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {checkingFavorite ? (
              <Badge className="bg-gray-400 text-white">
                <Calendar className="w-3 h-3 mr-1" />
                Checking...
              </Badge>
            ) : (
              <Badge className="bg-gold text-white">
                <Calendar className="w-3 h-3 mr-1" />
                {isActuallyFavorited ? 'Favorite Location' : 'Active Location'}
              </Badge>
            )}
            
            {!isActuallyFavorited && user && !checkingFavorite && (
              <Dialog open={isFavoriteDialogOpen} onOpenChange={setIsFavoriteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-gold text-gold hover:bg-muted">
                    <Star className="w-4 h-4 mr-1" />
                    Add to Favorites
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add to Favorites</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="favoriteName">Favorite Name</Label>
                      <Input
                        id="favoriteName"
                        value={favoriteFormData.favoriteName}
                        onChange={(e) => setFavoriteFormData(prev => ({ ...prev, favoriteName: e.target.value }))}
                        placeholder="e.g., My favorite Milky Way spot"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="notes">Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        value={favoriteFormData.notes}
                        onChange={(e) => setFavoriteFormData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Add notes about this location, parking info, best viewing spots, etc."
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button onClick={onStarLocation} className="flex-1">
                        <Star className="w-4 h-4 mr-2" />
                        Add to Favorites
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setIsFavoriteDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
